import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext.js';
import { SubscriptionBanner } from './SubscriptionBanner.js';
import { UserMenu, type NavGroup } from './UserMenu.js';
import { NavSidebar } from './NavSidebar.js';

/**
 * Shell for /me/* routes — the employee self-service portal.
 *
 * Same visual chrome as AuthedLayout (top header + grouped left sidebar + main
 * content area) but with a stripped-down nav: only the things an employee can
 * do. No Employees/Payroll runs/FX rates etc — those are admin pages and would
 * 401/403 on /v1/* if the employee tried them anyway, but better to not even
 * surface them. Account & security lives in the header user menu, mirroring
 * AuthedLayout.
 *
 * The visual symmetry is intentional: HR admins and employees share a mental
 * model of "LumoHR has a sidebar and a header". Different content; same shape.
 */
export function EmployeeLayout({ children }: { children: ReactNode }): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Mirror image of AuthedLayout's safety net: a non-employee somehow ending up
  // in the /me portal gets bounced to /dashboard. The meLayoutRoute beforeLoad
  // handles this via JWT decode, but if that path missed (stale bundle, decode
  // failure, etc.) the runtime backstop catches it on first render.
  useEffect(() => {
    // `it` shares the /me portal for its own assets/profile (ADR-0025 D4).
    if (user && user.role !== 'employee' && user.role !== 'it') {
      void navigate({ to: '/dashboard' });
    }
  }, [user, navigate]);

  async function handleLogout(): Promise<void> {
    await logout();
    await navigate({ to: '/login' });
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <header className="border-b border-gray-900 px-6 py-3 flex items-center justify-between">
        <Link to="/me" className="font-serif text-xl">
          LumoHR
        </Link>
        <div className="flex items-center gap-4 text-sm">
          {user ? (
            <UserMenu user={user} onLogout={handleLogout} />
          ) : (
            <button
              type="button"
              onClick={handleLogout}
              className="rounded border border-gray-700 px-3 py-1 text-xs hover:bg-gray-900"
            >
              Sign out
            </button>
          )}
        </div>
      </header>
      <SubscriptionBanner mode="employee" />
      <div className="flex flex-1 min-h-0">
        {/* ADR-0025 D8 — an `it` custodian in the /me portal sees only their own
            assets + profile + a way back to the Assets admin surface. */}
        <NavSidebar groups={user?.role === 'it' ? IT_ME_NAV_GROUPS : EMPLOYEE_NAV_GROUPS} ariaLabel="Employee navigation" storageKey={user?.role === 'it' ? 'lumohr.nav.it-me' : 'lumohr.nav.employee'} />
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Employee sidebar — three groups. Security lives in the header UserMenu.
// "Leave approvals" stays in the employee nav: any employee may be assigned as
// another's manager (ADR D20 sticky-approver), and the page filters server-side
// by approverId — non-approvers see an empty queue, so exposing it is safe.
// ---------------------------------------------------------------------------

const EMPLOYEE_NAV_GROUPS: NavGroup[] = [
  {
    label: 'Me',
    items: [
      { label: 'Home', to: '/me' },
      { label: 'My profile', to: '/me/profile' },
      { label: 'My documents', to: '/me/documents' },
      { label: 'My assets', to: '/me/assets' },
      { label: 'Work-tool requests', to: '/me/asset-requests' },
      { label: 'Asset approvals', to: '/me/asset-approvals' },
    ],
  },
  {
    label: 'Pay',
    items: [
      { label: 'My payslips', to: '/me/payslips' },
      { label: 'My loans', to: '/me/loans' },
      { label: 'Earned wage access', to: '/me/ewa-draws' },
      { label: 'Expense claims', to: '/me/expense-claims' },
      // Shown to all employees — the API self-filters approvals by approver id,
      // so a non-approver just sees an empty queue (the sticky-approver pattern).
      { label: 'Expense approvals', to: '/me/expense/approvals' },
    ],
  },
  {
    label: 'Leave',
    items: [
      { label: 'My leave', to: '/me/leave' },
      { label: 'Leave approvals', to: '/me/leave/approvals' },
    ],
  },
  {
    label: 'Attendance',
    items: [
      { label: 'My attendance', to: '/me/attendance' },
      { label: 'My timesheet', to: '/me/timesheet' },
      { label: 'Overtime approvals', to: '/me/attendance/overtime/approvals' },
    ],
  },
  {
    label: 'Performance',
    items: [
      { label: 'My performance', to: '/me/performance' },
      { label: 'Reviews to complete', to: '/me/performance/reviews' },
    ],
  },
  {
    label: 'Recruitment',
    items: [{ label: 'Approvals', to: '/me/recruitment/approvals' }],
  },
];

// ADR-0025 D8 — the `it` custodian's /me nav: their own assets + profile, and a
// link back to the Assets admin surface (their primary shell).
export const IT_ME_NAV_GROUPS: NavGroup[] = [
  {
    label: 'Me',
    items: [
      { label: 'My assets', to: '/me/assets' },
      { label: 'My profile', to: '/me/profile' },
    ],
  },
  {
    label: 'Assets',
    items: [{ label: '← Asset admin', to: '/assets/catalog' }],
  },
];
